/**
 * One exercise inside the day editor: its sets, and what can be done to them.
 *
 * The `supported` toggle is the reason this screen exists. Shorthand can only mark a
 * whole message supported (D-25), so "the last two sets had a spot" is expressible here
 * and nowhere else. It does not stack with an assisted catalog variant, which already
 * carries a halved factor (D-21) — the note under the toggle says so, because a score
 * quietly halving twice is exactly the kind of thing C-11 warns about.
 *
 * A set was a row in a five-column table, which at 390px meant a sideways scroll, an
 * 18px checkbox and a 10x20px delete target. It then became a block of two lines. Since
 * B-60 (D-111) it is one line again — tick, reps, weight, Supported, total — with the
 * units said once in a header row above the sets, and a per-set delete (where one is
 * offered) on a slim line of its own beneath. Nothing scrolls horizontally and every
 * target still clears 44px.
 *
 * The two numeric fields used to coerce on every keystroke —
 * `Math.max(0, Number(e.target.value))` — which turned an emptied field into "0" and a
 * pasted word into `NaN`, and `NaN` went on into `scoreSet`. They are `NumberField`s
 * now, which hold a string while it is being typed and commit only a value that passed
 * `checkNumber`.
 *
 * On the `done` path (B-34's Today screen) three controls behave differently from
 * `/day/:date`, because the safety argument there is different: nothing is stored until
 * a tick, so destroying a stored set has to go through the one gesture the spec sanctions
 * — unticking — and never through a bulk or freehand one.
 *
 * - **"Remove exercise"** is hidden whenever the row has any stored set (`canRemove` is
 *   false) — a mis-tap next to an exercise the bot logged this morning must not mass-
 *   delete it with no confirmation and no undo (re-ticking restores a single set; it does
 *   not restore what a whole-row delete took). A purely proposed row — nothing in it
 *   stored yet — still offers it, since dropping a proposal destroys nothing.
 * - **"Add a set"** appends a set the normal way when `done` is absent; when `done` is
 *   given it instead calls `onAddSet`, because `onChange` there is Today's `onEdit`,
 *   which only reconciles sets already present before and after an edit — it has no
 *   notion of a set appearing in the middle of the list. The new set is proposed
 *   (unticked) and held in the caller's local state; nothing is written until it is
 *   ticked.
 * - Per-set **"Remove"** stays hidden for a stored set on the `done` path (that is what
 *   unticking is for) but is shown for a set in `localSetIds` — one `onAddSet` itself
 *   produced and that was never stored, so removing it destroys nothing either.
 *
 * On both paths "Add a set" is a button under the last set, and the new set is the last
 * row (B-62, D-114, reversing B-59's circled "+" at the top). With a set on one line
 * (B-60) a card of six or seven sets is short enough that the button stays in reach.
 *
 * `DayPage` never passes `done`, so none of this changes anything there. Nor `position`
 * (B-57), which is Today's "2 of 5" — the day editor shows every card at once.
 */

import { useState } from "react";
import type { CatalogExercise, ExerciseSet, LoggedExercise } from "@altrtrack/core";
import { exerciseById, formatNumber, scoreSet } from "@altrtrack/core";
import { NumberField } from "./NumberField.js";
import type { Bounds } from "../fields.js";

export function ExerciseEditor({
  exercise,
  catalog,
  bodyweightKg,
  onChange,
  onRemove,
  done,
  onToggleDone,
  canRemove = true,
  onAddSet,
  onRemoveLocalSet,
  localSetIds,
  position,
}: {
  exercise: LoggedExercise;
  /** The catalog entry, for the factor and the reps/seconds distinction. */
  catalog: Pick<CatalogExercise, "bodyweightFactor" | "measure">;
  bodyweightKg: number;
  onChange: (next: LoggedExercise) => void;
  onRemove: () => void;
  /** Per-set tick state, keyed by set id. Absent ⇒ no checkboxes at all. */
  done?: ReadonlyMap<string, boolean>;
  /** Called when a checkbox is toggled. Required when `done` is given. */
  onToggleDone?: (setId: string, next: boolean) => void;
  /**
   * Whether "Remove exercise" is offered at all. Ignored when `done` is absent — there
   * it is always offered, as before. When `done` is given, pass `false` for any row that
   * has a stored set (Critical 2): the whole-row delete has no confirmation and no undo,
   * and unticking is the one destructive gesture the spec sanctions.
   */
  canRemove?: boolean;
  /**
   * "Add a set" on the `done` path: builds a proposed set the normal way and hands it up
   * rather than splicing it into `exercise` directly, since the caller's local state (not
   * `onChange`) is what needs to hold it until it is ticked. Ignored when `done` is
   * absent — there "Add a set" goes through `onChange` as before.
   */
  onAddSet?: (set: ExerciseSet) => void;
  /** A not-yet-stored set produced by `onAddSet`, so its per-set "Remove" may show. */
  onRemoveLocalSet?: (setId: string) => void;
  localSetIds?: ReadonlySet<string>;
  /**
   * Where this card sits in Today's list, shown after the name as "2 of 5" (B-57, D-108).
   * Today shows one card at a time, so without it nothing on screen says how many are
   * left. `DayPage` shows every exercise at once and passes nothing.
   */
  position?: { index: number; count: number };
}) {
  const timed = catalog.measure === "duration";

  /*
   * Which unit a timed exercise is TYPED in (B-42, D-92). Stored is always seconds — this
   * is a property of the field, not of the data.
   *
   * It starts from what the sets already are, by the same threshold the display uses
   * (D-88): a block whose every set is minutes-scale opens in minutes, a plank opens in
   * seconds. After that only the user moves it, never a keystroke — a field that changes
   * its own unit as the number crosses 120 would reinterpret what is already typed, which
   * is the "frozen field" class of defect D-65 exists to keep out.
   */
  const [typedIn, setTypedIn] = useState<"seconds" | "minutes">(() =>
    timed && exercise.sets.length > 0 && exercise.sets.every((s) => s.reps >= 120)
      ? "minutes"
      : "seconds",
  );
  const inMinutes = timed && typedIn === "minutes";
  const unit = timed ? typedIn : "reps";

  /*
   * The ceilings are sanity checks, not sport. An hour is longer than any plank and a
   * thousand reps longer than any set, so a number above either is a typo — most often a
   * doubled keystroke — and saying so beats storing it and scoring it. The minutes field
   * is the same hour, said differently, and is the one count that may be fractional: half
   * a minute is a real answer where half a rep is not.
   */
  const countBounds: Bounds = inMinutes
    ? { min: 0.1, max: 60, unit }
    : { min: 1, max: timed ? 3600 : 1000, integer: true, unit };

  /*
   * Seconds are what `reps` holds, in both directions. The round is what keeps a minutes
   * field from writing 149.99999999999997 for 2.5 — the float noise C-23 is about, here
   * caught before it reaches a document rather than after.
   */
  const toStored = (typed: number) => (inMinutes ? Math.round(typed * 60) : typed);
  const toTyped = (stored: number) => (inMinutes ? Math.round((stored / 60) * 100) / 100 : stored);
  const weightBounds: Bounds = { min: 0, max: 500, unit: "weight" };

  const setSets = (sets: LoggedExercise["sets"]) => onChange({ ...exercise, sets });

  const patch = (index: number, change: Partial<LoggedExercise["sets"][number]>) =>
    setSets(exercise.sets.map((s, i) => (i === index ? { ...s, ...change } : s)));

  const addSet = () => {
    const last = exercise.sets[exercise.sets.length - 1];
    const set: ExerciseSet = {
      id: crypto.randomUUID(),
      reps: last?.reps ?? (catalog.measure === "duration" ? 60 : 10),
      weightKg: last?.weightKg ?? 0,
      supported: last?.supported ?? false,
    };
    if (done) onAddSet?.(set);
    else setSets([...exercise.sets, set]);
  };

  // The catalog's current name, so a rename (B-66) reaches days logged before it; the
  // stored copy only for an id this build does not know.
  const name = exerciseById(exercise.catalogId)?.name ?? exercise.name;

  return (
    <section className="exercise-edit">
      <header className="exercise-edit-head">
        <h2 className="title">
          {name}
          {position && (
            <span className="exercise-edit-position">
              {" "}
              · {position.index} of {position.count}
            </span>
          )}
        </h2>
        <span className="exercise-edit-actions">
          {(!done || canRemove) && (
            /*
              An icon rather than the words (D-95): five red "Remove exercise" links
              dominated Today before anything was logged. The name the words carried is
              kept for a screen reader and a long-press, as `aria-label` and `title`.
            */
            <button
              className="btn-icon-danger"
              type="button"
              aria-label={`Remove ${name}`}
              title="Remove exercise"
              onClick={onRemove}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </span>
      </header>

      {exercise.sets.length > 0 && (
        /*
          One grid for every set, with the rows as subgrids (B-60, D-111), so a column is
          as wide as its widest cell in ANY row and the units can be said once, above the
          fields, instead of once per set. That is what pays for reps, weight, Supported
          and the total all fitting on one line at 393px.
        */
        <div className={`set-grid${timed ? " set-grid-timed" : ""}`}>
          <div className="set-head" aria-hidden="true">
            <span className="set-unit">{timed ? "time" : "reps"}</span>
            <span className="set-unit">kg</span>
          </div>

          {exercise.sets.map((set, index) => (
            <div className={`set${done && !done.get(set.id) ? " set-proposed" : ""}`} key={set.id}>
              <div className="set-line">
                {done ? (
                  <button
                    className="tick"
                    type="button"
                    role="checkbox"
                    aria-checked={done.get(set.id) ?? false}
                    aria-label={`Set ${index + 1} done`}
                    onClick={() => onToggleDone?.(set.id, !(done.get(set.id) ?? false))}
                  >
                    <span className="tick-box" aria-hidden="true" />
                  </button>
                ) : (
                  <span className="set-index">{index + 1}</span>
                )}
                <NumberField
                  value={toTyped(set.reps)}
                  bounds={countBounds}
                  label={`Set ${index + 1} ${unit}`}
                  onCommit={(typed) => patch(index, { reps: toStored(typed) })}
                />
                {timed && (
                  /*
                    The unit sits in the row, beside the number it qualifies (B-63, D-115):
                    a boxed select over the column read as a second row of fields, so a
                    one-set treadmill looked like a two-row set. It is still one piece of
                    state for the whole exercise — every row's switch shows and moves the
                    same unit, and moving it re-renders the stored seconds, never edits them.
                    A tap rather than a select, because there are only two answers.
                  */
                  <button
                    className="set-unit-toggle"
                    type="button"
                    aria-label={`Set ${index + 1} time in ${typedIn}, switch to ${inMinutes ? "seconds" : "minutes"}`}
                    onClick={() => setTypedIn(inMinutes ? "seconds" : "minutes")}
                  >
                    {inMinutes ? "min" : "sec"}
                  </button>
                )}
                <NumberField
                  value={set.weightKg}
                  bounds={weightBounds}
                  label={`Set ${index + 1} weight in kilograms`}
                  onCommit={(weightKg) => patch(index, { weightKg })}
                />
                <button
                  className="toggle"
                  type="button"
                  aria-pressed={set.supported}
                  aria-label={`Set ${index + 1} was supported`}
                  onClick={() => patch(index, { supported: !set.supported })}
                >
                  <span className="toggle-box" aria-hidden="true" />
                  <span className="toggle-word">Supported</span>
                </button>
                <span className="set-score">{formatNumber(scoreSet(set, catalog, bodyweightKg))}</span>
              </div>

              {(!done || localSetIds?.has(set.id)) && (
                /*
                  Its own slim line under the row, right-aligned beneath the total (D-111):
                  there is no 44px left on the row at 393px, and a delete squeezed in beside
                  the Supported toggle is the mis-tap this card has always kept away from.
                */
                <div className="set-foot">
                  <button
                    className="btn-link-danger quiet"
                    type="button"
                    aria-label={`Remove set ${index + 1}`}
                    onClick={() =>
                      done ? onRemoveLocalSet?.(set.id) : setSets(exercise.sets.filter((_, i) => i !== index))
                    }
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(!done || onAddSet) && (
        <button className="btn btn-small spaced" type="button" onClick={addSet}>
          Add a set
        </button>
      )}
    </section>
  );
}
